import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { stableStringify } from '@Utils/searchParams';
import service from '../service';

export type FilterOp =
  | 'eq'
  | 'ne'
  | 'in'
  | 'nin'
  | 'gte'
  | 'lte'
  | 'gt'
  | 'lt'
  | 'exists'
  | 'notExists'
  | 'within_last'
  | 'older_than';

export type TemporalUnit =
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years';

export type TemporalWindow = { amount: number; unit: TemporalUnit };

export type FilterClause = {
  field: string;
  op: FilterOp;
  value?: string | number | boolean | Array<string | number> | TemporalWindow;
  anchor?: { type: 'now' | 'absolute'; value?: string | number };
};

export type SearchFieldType = 'string' | 'number' | 'date' | 'boolean' | 'enum';

export type SearchFieldDescriptor = {
  name: string;
  label: string;
  type: SearchFieldType;
  filterable: boolean;
  sortable: boolean;
  facetable: boolean;
  valuesUri?: string;
};

export type IndexSchema = {
  uid: string;
  label: string;
  searchable: string[];
  sortable: string[];
  defaultSort?: string;
  fields: SearchFieldDescriptor[];
  labels: Record<string, string>;
};

export type OmniIndexResult = {
  index: string;
  label: string;
  hits: Record<string, any>[];
  totalHits: number;
  uniqueFishCount: number | null;
  uniqueFishCapped: boolean;
  facetDistribution: Record<string, Record<string, number>> | null;
  facetStats: Record<string, { min: number; max: number }> | null;
};

export type OmniSearchResponse = { results: OmniIndexResult[] };

export type OmniSearchBody = {
  q?: string;
  indexes?: string[];
  perIndex?: Record<string, { filters?: FilterClause[]; sort?: string[] }>;
  facets?: Record<string, string[]>;
  limit?: number;
  offset?: number;
  countUniqueFish?: boolean;
};

/** Runs an omni-search query (cross-index or scoped) and returns grouped results. */
export function useOmniSearch(body: OmniSearchBody, enabled = true) {
  const key = useMemo(() => stableStringify(body), [body]);
  return useQuery<OmniSearchResponse>({
    queryKey: ['omni-search', key],
    queryFn: () => service.omniSearch(body),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/** Loads an index's filter/sort schema (built from live Meili settings + registry). */
export function useSearchSchema(index?: string) {
  return useQuery<{ data: IndexSchema }>({
    queryKey: ['search-schema', index],
    queryFn: () => service.getSearchSchema(index!),
    enabled: !!index,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Loads values for a filterable field (for enum/dropdown filters). With `q`, this performs a
 * server-side facet search (type-ahead) so high-cardinality fields stay fully searchable.
 */
export function useSearchFieldValues(index: string, field: string, q = '', enabled = true) {
  return useQuery<{ data: Array<{ value: string; label: string }> }>({
    queryKey: ['search-field-values', index, field, q],
    queryFn: () => service.getSearchFieldValues(index, field, q || undefined),
    enabled: enabled && !!index && !!field,
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
